import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ShareNetwork, SealCheck, Gift, UploadSimple, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

const STEPS = [
  { icon: ShareNetwork, title: "Get referred", text: "An existing Kenny Web Design client shares their unique referral code with you." },
  { icon: SealCheck, title: "Verify your referral", text: "Enter the code and upload proof (an email, message, or invoice) confirming the referral." },
  { icon: Gift, title: "Enjoy your discount", text: "Once verified, your discount is applied instantly and your referrer earns a reward too." },
];

export default function Referral() {
  const [code, setCode] = useState("");
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", business_name: "", phone: "", notes: "" });
  const [proof, setProof] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const fileRef = useRef(null);

  const validate = async () => {
    if (!code.trim()) return;
    setValidating(true);
    setValidation(null);
    try {
      const res = await api.post("/referral/validate", { code });
      setValidation(res.data);
    } catch {
      setValidation({ valid: false, message: "Could not validate code. Try again." });
    } finally {
      setValidating(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validation?.valid) {
      toast.error("Please validate a working referral code first.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("business_name", form.business_name);
      fd.append("phone", form.phone);
      fd.append("notes", form.notes);
      fd.append("code", code);
      if (proof) fd.append("proof", proof);
      const res = await api.post("/referral/redeem", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSuccess(res.data);
      toast.success("Referral verified! Discount applied.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-black/15 px-4 py-3 outline-none focus:border-black transition-colors";

  if (success) {
    return (
      <div>
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-28 sm:py-36">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl mx-auto text-center" data-testid="referral-success">
            <CheckCircle size={64} weight="fill" className="mx-auto text-black" />
            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-black tracking-tighter">You're all set!</h1>
            <p className="mt-4 text-lg text-zinc-600">
              Your <b>{success.discount_percent}% discount</b> has been applied
              {success.referrer_business ? <> (referred by <b>{success.referrer_business}</b>)</> : null}. We've emailed you
              a confirmation and our team will reach out shortly.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        overline="Referral Program"
        title="Get rewarded for great referrals."
        subtitle="Refer a business and you both win. New clients redeem a discount, and referring clients earn rewards. It's our way of saying thank you."
      />

      {/* How it works */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">How it works</span>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} delay={i * 0.08} className="bg-white p-8 sm:p-10">
                <div className="flex items-center gap-4">
                  <span className="font-display text-4xl font-black tracking-tighter text-zinc-200">0{i + 1}</span>
                  <Icon size={32} weight="duotone" />
                </div>
                <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-zinc-600 leading-relaxed">{s.text}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Redemption */}
      <section className="bg-white border-y border-black/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Redeem a referral code</h2>
              <p className="mt-4 text-zinc-600 leading-relaxed">
                Enter your code below and validate it. Then complete the form and upload proof that an existing client referred you.
              </p>
              <p className="mt-4 text-sm text-zinc-500">Try demo codes: <b>WELCOME10</b> or <b>BELLA15</b>.</p>
            </div>

            <div className="lg:col-span-8">
              <form onSubmit={submit} className="border border-black/10 bg-[#FAFAFA] p-6 sm:p-10" data-testid="referral-form">
                {/* code */}
                <label className="text-sm font-medium">Referral code</label>
                <div className="mt-2 flex gap-3">
                  <input
                    data-testid="referral-code-input"
                    value={code}
                    onChange={(e) => { setCode(e.target.value.toUpperCase()); setValidation(null); }}
                    placeholder="ENTER CODE"
                    className={`${inputCls} uppercase tracking-widest font-medium`}
                  />
                  <button
                    type="button"
                    data-testid="referral-validate-btn"
                    onClick={validate}
                    disabled={validating}
                    className="shrink-0 rounded-none bg-black text-white font-medium px-6 hover:bg-zinc-800 transition-colors disabled:opacity-50"
                  >
                    {validating ? "Checking…" : "Validate"}
                  </button>
                </div>
                {validation && (
                  <div
                    data-testid="referral-validation-message"
                    className={`mt-3 text-sm flex items-center gap-2 ${validation.valid ? "text-black" : "text-red-600"}`}
                  >
                    {validation.valid ? <CheckCircle size={18} weight="fill" /> : <X size={18} />}
                    {validation.message}
                  </div>
                )}

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium">Your name *</label>
                    <input required data-testid="referral-name" className={`mt-2 ${inputCls}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email *</label>
                    <input required type="email" data-testid="referral-email" className={`mt-2 ${inputCls}`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Business name</label>
                    <input data-testid="referral-business" className={`mt-2 ${inputCls}`} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Phone</label>
                    <input data-testid="referral-phone" className={`mt-2 ${inputCls}`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="text-sm font-medium">Notes (optional)</label>
                  <textarea rows={3} data-testid="referral-notes" className={`mt-2 ${inputCls} resize-none`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Who referred you? Any context helps." />
                </div>

                {/* proof upload */}
                <div className="mt-5">
                  <label className="text-sm font-medium">Proof of referral</label>
                  <div
                    data-testid="referral-upload-zone"
                    onClick={() => fileRef.current?.click()}
                    className="mt-2 border border-dashed border-black/25 bg-white px-6 py-8 text-center cursor-pointer hover:border-black transition-colors"
                  >
                    <input ref={fileRef} type="file" data-testid="referral-proof-input" className="hidden" accept="image/*,.pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} />
                    {proof ? (
                      <div className="flex items-center justify-center gap-2 font-medium">
                        <CheckCircle size={20} weight="fill" /> {proof.name}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-zinc-500">
                        <UploadSimple size={28} />
                        <span className="text-sm">Upload an email, invoice, or message (PNG, JPG, PDF · max 5MB)</span>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="referral-submit-btn"
                  disabled={submitting}
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-black text-white font-medium px-8 py-4 hover:bg-zinc-800 transition-colors disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Redeem discount"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
